import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { DEFAULT_DIALOG_NO_PADDING_CLASS } from "@merri/common";
import { Subject, take, takeUntil } from "rxjs";
import { CommonWorkflowStep, LoggingService } from "src/app/core";
import { VisionboardItemSelectionDialogComponent } from "../../../components";
import { VisionboardCategoriesService } from "../../../services";
import { VisionboardGlobalItemSearchService, VisionboardItemAddRemoveButtonService } from "../../services";
import {
  GlobalSearchSelection,
  VisionboardGlobalSearchResultComponent,
} from "../visionboard-global-search-result/visionboard-global-search-result.component";
import { ForceVisionboardItemAddRemoveButton } from "../visionboard-item-add-remove-button/visionboard-item-add-remove-button.component";

export const VB_GLOBAL_SEARCH_DIALOG_ID: string = "visionboard-global-search";

export interface VisionboardGlobalSearchDialogInput {
  itemCustomAddRemoveTemplate: TemplateRef<void>;
  forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton;
  source: string;
}

const Workflow = "VBGlobalSearch";
const WorkflowSource = "GlobalSearchDialog";

@Component({
    selector: "app-visionboard-global-search-dialog",
    templateUrl: "./visionboard-global-search-dialog.component.html",
    styleUrls: ["./visionboard-global-search-dialog.component.scss"],
    standalone: false
})
export class VisionboardGlobalSearchDialogComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  searchTerm: string;

  @ViewChild(VisionboardGlobalSearchResultComponent) searchResultComponent: VisionboardGlobalSearchResultComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public input: VisionboardGlobalSearchDialogInput,
    private _dialog: MatDialogRef<VisionboardGlobalSearchDialogComponent>,
    private _visionboardGlobalItemSearchService: VisionboardGlobalItemSearchService,
    private _visionboardCategoriesService: VisionboardCategoriesService,
    private _visionboardItemAddRemoveButtonService: VisionboardItemAddRemoveButtonService,
    private _matDialog: MatDialog,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    this._dialog.addPanelClass([DEFAULT_DIALOG_NO_PADDING_CLASS]);
    this._dialog.disableClose = true;

    this._dialog
      .keydownEvents()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: event => {
          if (event.key === "Escape") {
            this._close("EscapeKey");
          }
        },
      });

    this._dialog
      .backdropClick()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({ next: () => this._close("Backdrop") });

    this.searchTerm = this._visionboardGlobalItemSearchService.getSearchTerm();

    this._loggingService.logWorkflow(
      "VB Global Search Dialog init",
      "VisionboardGlobalSearchDialogComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoad,
      this.input.source ?? WorkflowSource,
      {
        SearchTerm: this.searchTerm,
      },
    );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onGlobalSearchResultSelect(result: GlobalSearchSelection): void {
    if (result.item) {
      this._loggingService.logWorkflow(
        "VB Global Search dialog result select item",
        "VisionboardGlobalSearchDialogComponent.onGlobalSearchResultSelect",
        Workflow,
        "GlobalSearchResultSelectItem",
        this.input.source ?? WorkflowSource,
        {
          ItemName: result.item.skuName,
          ItemSkuId: `${result.item.variantSkuId}`,
        },
      );

      this._close("ItemSelect", result);
      return;
    }

    if (this._visionboardCategoriesService.setSelectedCategoryBySubCategory(result.category)) {
      this._loggingService.logWorkflow(
        "VB Global Search dialog result select category",
        "VisionboardGlobalSearchDialogComponent.onGlobalSearchResultSelect",
        Workflow,
        "GlobalSearchResultSelectCategory",
        this.input.source ?? WorkflowSource,
        {
          CategorySelected: result.category[1],
        },
      );

      const dialog = this._matDialog.open(VisionboardItemSelectionDialogComponent, {
        data: {},
        autoFocus: false,
      });

      dialog
        .afterClosed()
        .pipe(take(1))
        .subscribe(result => {
          if (result) {
            this._visionboardItemAddRemoveButtonService.addItemClick({
              item: result,
            });

            this._visionboardGlobalItemSearchService.itemSelect(result);
          }
        });
    }

    this._close("CategorySelect", result);
  }

  onSearch(searchTerm: string): void {
    this._visionboardGlobalItemSearchService.search(searchTerm);
  }

  onCloseClick(): void {
    this._close("XButton");
  }

  private _close(source: string, result?: GlobalSearchSelection): void {
    this._loggingService.logWorkflow(
      "Global search dialog close",
      "VisionboardGlobalSearchDialogComponent._close",
      Workflow,
      CommonWorkflowStep.ModalClose,
      source,
    );

    this._dialog.close(result);
  }
}
